import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useCustomer } from "./CustomerContext";
import {
  assignEnvironmentDetails,
  setCurrentEnvironment,
} from "../services/user.service";
import { EnvironmentSummary, setEnvironmentState } from "../services/environment.store";
import {
  getScopeEnvironmentId,
  pickActiveScopeId,
  setScopeEnvList,
  setScopeEnvironmentId,
} from "../services/activeScope";

interface EnvironmentContextValue {
  environments: EnvironmentSummary[];
  activeEnvironmentId?: number;
  activeEnvironment?: EnvironmentSummary;
  isLoading: boolean;
  isSwitching: boolean;
  selectEnvironment: (environmentId: number) => Promise<void>;
  refreshEnvironments: () => Promise<void>;
}

const EnvironmentContext = createContext<EnvironmentContextValue>({
  environments: [],
  activeEnvironmentId: undefined,
  activeEnvironment: undefined,
  isLoading: false,
  isSwitching: false,
  selectEnvironment: async () => undefined,
  refreshEnvironments: async () => undefined,
});

export const EnvironmentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    activeCustomerId,
    userDetails,
    refreshCustomers,
    isLoading: isCustomerLoading,
  } = useCustomer();

  const [environments, setEnvironments] = useState<EnvironmentSummary[]>([]);
  const [activeEnvironmentId, setActiveEnvironmentId] = useState<
    number | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const persistedDefaultsRef = React.useRef<Record<number, boolean>>({});

  useEffect(() => {
    if (activeCustomerId === undefined) {
      persistedDefaultsRef.current = {};
      return;
    }

    if (!(activeCustomerId in persistedDefaultsRef.current)) {
      persistedDefaultsRef.current[activeCustomerId] = false;
    }
  }, [activeCustomerId]);

  const persistDefaultEnvironment = useCallback(
    async (environmentId: number, customerId: number) => {
      try {
        await setCurrentEnvironment(environmentId);
        await refreshCustomers();
        persistedDefaultsRef.current[customerId] = true;
      } catch (error) {
        console.error("Failed to persist default environment", error);
      }
    },
    [refreshCustomers]
  );

  const resolveAndPersistActiveEnvironment = useCallback(
    async (availableEnvironments: EnvironmentSummary[]) => {
      const userEnvironmentId =
        typeof userDetails?.current_environment_id === "number"
          ? userDetails.current_environment_id
          : undefined;

      const environmentFromUser =
        userEnvironmentId &&
        availableEnvironments.some(
          (environment) => environment.environment_id === userEnvironmentId
        )
          ? userEnvironmentId
          : undefined;

      // Keep this tab's environment: the user's current one may have been switched in another tab.
      const resolvedEnvironmentId = pickActiveScopeId(
        availableEnvironments.map((environment) => environment.environment_id),
        getScopeEnvironmentId(),
        userEnvironmentId
      );

      if (
        environmentFromUser &&
        activeCustomerId !== undefined &&
        !persistedDefaultsRef.current[activeCustomerId]
      ) {
        persistedDefaultsRef.current[activeCustomerId] = true;
      }

      const shouldPersistDefault =
        !environmentFromUser &&
        resolvedEnvironmentId !== undefined &&
        activeCustomerId !== undefined &&
        !persistedDefaultsRef.current[activeCustomerId];

      if (shouldPersistDefault) {
        await persistDefaultEnvironment(resolvedEnvironmentId, activeCustomerId);
      }

      setActiveEnvironmentId(resolvedEnvironmentId);
      setScopeEnvironmentId(resolvedEnvironmentId);
    },
    [
      activeCustomerId,
      persistDefaultEnvironment,
      refreshCustomers,
      userDetails?.current_environment_id,
    ]
  );

  const hydrateEnvironments = useCallback(async () => {
    if (!activeCustomerId) {
      setEnvironments([]);
      setActiveEnvironmentId(undefined);
      setScopeEnvList(null);
      setScopeEnvironmentId(null);
      return;
    }

    setIsLoading(true);
    try {
      const response = await assignEnvironmentDetails(activeCustomerId);
      const availableEnvironments = Array.isArray(response) ? response : [];
      setEnvironments(availableEnvironments);
      setScopeEnvList(JSON.stringify(availableEnvironments));
      await resolveAndPersistActiveEnvironment(availableEnvironments);
    } catch (error) {
      console.error("Failed to load environments", error);
      setEnvironments([]);
      setActiveEnvironmentId(undefined);
      setScopeEnvList(null);
      setScopeEnvironmentId(null);
    } finally {
      setIsLoading(false);
    }
  }, [activeCustomerId, resolveAndPersistActiveEnvironment]);

  useEffect(() => {
    if (isCustomerLoading) {
      return;
    }

    if (userDetails === undefined) {
      return;
    }

    hydrateEnvironments();
  }, [
    activeCustomerId,
    hydrateEnvironments,
    isCustomerLoading,
    userDetails,
  ]);

  const selectEnvironment = useCallback(
    async (environmentId: number) => {
      if (environmentId === activeEnvironmentId) {
        return;
      }

      if (!environments.some((env) => env.environment_id === environmentId)) {
        throw new Error("Environment not found in available environments");
      }

      setIsSwitching(true);
      try {
        await setCurrentEnvironment(environmentId);
        setActiveEnvironmentId(environmentId);
        // Before refreshCustomers: its re-hydrate keeps whatever this tab has stored.
        setScopeEnvironmentId(environmentId);
        await refreshCustomers();
      } catch (error) {
        console.error("Failed to switch environment", error);
        throw error;
      } finally {
        setIsSwitching(false);
      }
    },
    [activeEnvironmentId, environments, refreshCustomers]
  );

  const refreshEnvironments = useCallback(async () => {
    await hydrateEnvironments();
  }, [hydrateEnvironments]);

  useEffect(() => {
    setEnvironmentState({
      environments,
      activeEnvironmentId,
    });
  }, [environments, activeEnvironmentId]);

  const value = useMemo(() => {
    const activeEnvironment = environments.find(
      (environment) => environment.environment_id === activeEnvironmentId
    );

    return {
      environments,
      activeEnvironmentId,
      activeEnvironment,
      isLoading,
      isSwitching,
      selectEnvironment,
      refreshEnvironments,
    };
  }, [
    environments,
    activeEnvironmentId,
    isLoading,
    isSwitching,
    selectEnvironment,
    refreshEnvironments,
  ]);

  return (
    <EnvironmentContext.Provider value={value}>
      {children}
    </EnvironmentContext.Provider>
  );
};

export const useEnvironment = () => useContext(EnvironmentContext);
